/**
 * TRAC-919 — the resumed panel must tell the story of the handoff the customer is in.
 *
 * The chat panel follows a visitor from page to page, and a handoff started on one page
 * is resumed on the next. The LIVE plumbing already survives that (TRAC-732 #15 asks the
 * server whether the session is mid-handoff and re-arms the poll), but the turns that
 * told the story did not: the "Connecting you to our Sales team now…" turn and the band
 * header are appended by the handoff handlers, not by the send pipeline, so nothing
 * wrote them down. Come back mid-wait and the panel showed the department menu as the
 * latest message -- its four buttons alive again, directly above a live conversation --
 * and none of pick / connecting / connected. Tapping one of those buttons fired a
 * second request from inside the first.
 *
 * Two halves fix that. The handlers now persist as they go (see persistTranscript in
 * ChatInterface). And this function, run on resume with the server's word on the
 * handoff, brings whatever WAS stored in line with it: a missing connecting turn and
 * band header are re-created from the state, and if a rep is already in the room the
 * "Connecting…" reads "Connected ✅" and the header names them -- exactly the flips
 * agent_joined would have made had the page been open to see it.
 *
 * Pure: messages in, messages out. Nothing here touches React or storage.
 */
import type { Message } from '../../types/chat.types';

/** What the server says about the handoff on this session (see resumeHandoffIfActive). */
export interface HandoffSnapshot {
  active: boolean;
  /** 'human' once a rep is in the conversation; anything else = still connecting / queued. */
  mode: string;
  agentName: string | null;
}

export interface ReconcileResult {
  messages: Message[];
  /** True when anything was added or flipped -- the caller persists in that case. */
  changed: boolean;
  /** The connecting turn for this handoff, so a later agent_joined can flip it. */
  connectingId: string | null;
  /** The band header for this handoff, so a later agent_joined can name the rep. */
  enterHumanDivId: string | null;
}

// TRAC-732: the "Connecting you now…" turn names the team we're routing to once
// it's known (from the popup, the agent's pick, or the backend's classification).
export const connectingLabel = (dept?: string | null): string => {
  const d = (dept || '').toLowerCase();
  // "Sales" / "Customer Care" stay Capitalized as proper team names.
  if (d === 'sales') return 'Connecting you to our Sales team now…';
  if (d === 'customer care') return 'Connecting you to our Customer Care team now…';
  // Generic until the backend confirms which team actually gets it — we never name
  // a team optimistically (the intended team may be offline and get rerouted).
  return 'Connecting you to a Tire Expert now…';
};

/**
 * TRAC-1049 — which team a connecting turn names, for a tab that cannot know it otherwise.
 *
 * The team the customer picked is kept per TAB (the handoff attempt record lives in
 * sessionStorage), so only the tab that pressed the button has it. This heading is saved in the
 * transcript, which every tab of the same chat reads back — so it is the one place a second tab
 * can learn the team, and reading it here is what keeps the heading and the still-trying line
 * under it from naming different teams.
 *
 * The inverse of connectingLabel, read by comparing against the wording it writes rather than by
 * picking the text apart. Anything it did not write — the generic turn, a "Connected ✅" flip, a
 * transfer, wording from before a copy change — reads as unknown.
 */
export const connectingLabelDepartment = (content: unknown): 'Sales' | 'Customer Care' | null => {
  if (typeof content !== 'string') return null;
  for (const team of ['Sales', 'Customer Care'] as const) {
    if (content === connectingLabel(team)) return team;
  }
  return null;
};

const DEFAULT_REP = 'Tire Rack team';

const isEnterHuman = (m: Message) => m.responder === 'divider' && m.dividerKind === 'enter-human';
const isEndHuman = (m: Message) => m.responder === 'divider' && m.dividerKind === 'end-human';

/** The connecting turn's copy once the rep is in, or null when it already reads that way. */
export function connectedCopy(content: unknown): string | null {
  if (typeof content !== 'string') return null;
  if (content.startsWith('Connecting')) return 'Connected ✅';
  if (content.startsWith('Transferring')) return 'Transferred ✅';
  return null;
}

/**
 * Bring a stored transcript in line with a handoff the server says is still going.
 * Only the stretch belonging to THIS handoff is touched: everything after the last
 * closed band and after the last department menu. Earlier, completed live chats keep
 * their turns exactly as they were.
 */
export function reconcileHandoffTranscript(
  stored: Message[],
  state: HandoffSnapshot,
  now: number = Date.now(),
  confirmedDepartment: string | null = null,
): ReconcileResult {
  const untouched = (): ReconcileResult => ({
    messages: stored, changed: false, connectingId: null, enterHumanDivId: null,
  });
  if (!state.active) return untouched();

  // Where this handoff's story starts: after the last department menu, and never
  // before the end of a previous live chat.
  let start = 0;
  for (let i = stored.length - 1; i >= 0; i--) {
    if (stored[i].isRoutingPrompt || isEndHuman(stored[i])) { start = i + 1; break; }
  }
  const stretch = stored.slice(start);

  let messages = stored.slice();
  let changed = false;

  let connIdx = -1;
  for (let i = stretch.length - 1; i >= 0; i--) {
    if (stretch[i].isConnectingTurn) { connIdx = start + i; break; }
  }
  let divIdx = -1;
  for (let i = stretch.length - 1; i >= 0; i--) {
    if (isEnterHuman(stretch[i])) { divIdx = start + i; break; }
  }
  // Anything already inside the band (rep replies, queued customer messages) marks
  // where the story must sit -- BEFORE it, not appended under it.
  let anchor = -1;
  for (let i = 0; i < stretch.length; i++) {
    if (stretch[i].inHumanZone) { anchor = start + i; break; }
  }

  const repName = state.agentName || DEFAULT_REP;

  if (connIdx === -1) {
    // The team is not part of the server's snapshot, and naming one that turned out to be
    // rerouted would be a lie the customer could read — so this is generic unless the shared
    // attempt says the backend has already CONFIRMED the team (TRAC-1049). That is the same
    // moment the tab that asked fills its own heading in, so two tabs never disagree.
    const turn = {
      id: `connecting-${now}`,
      role: 'user',
      content: connectingLabel(confirmedDepartment),
      timestamp: new Date(now),
      isClientOnly: true,
      isConnectingTurn: true,
    } as Message;
    const at = anchor === -1 ? messages.length : anchor;
    messages.splice(at, 0, turn);
    connIdx = at;
    if (divIdx >= at) divIdx += 1;
    if (anchor !== -1) anchor += 1;
    changed = true;
  }

  // TRAC-1049: a heading STORED before the backend answered is generic, and the tab that asked
  // fills its own copy in without writing to any other tab. So a tab that read the transcript
  // during those seconds — a second tab opened right after the press, which is exactly what the
  // ticket describes — keeps "a Tire Expert" for the rest of the wait, and the still-trying line
  // under it reads both extensions. Bring it up to date from the shared attempt. Only the exact
  // generic wording is replaced, so a "Connected ✅", a transfer, or a heading that already names
  // a team is never touched.
  if (confirmedDepartment && messages[connIdx].content === connectingLabel(null)) {
    messages = messages.map((m, i) => (i === connIdx
      ? { ...m, content: connectingLabel(confirmedDepartment) } : m));
    changed = true;
  }

  if (divIdx === -1) {
    const header = {
      id: `div-${now}-resume`,
      role: 'assistant', content: '', timestamp: new Date(now),
      responder: 'divider', dividerKind: 'enter-human',
      dividerPending: true,
      agentName: repName,
      inHumanZone: true,
      isClientOnly: true,
    } as Message;
    // Right after the connecting turn -- which is where escalate puts it -- unless the
    // band already has messages, in which case it heads them.
    const at = anchor === -1 ? connIdx + 1 : anchor;
    messages.splice(at, 0, header);
    divIdx = at;
    changed = true;
  }

  if (state.mode === 'human') {
    // The rep arrived while nobody was looking. Make the same two flips agent_joined makes.
    const turn = messages[connIdx];
    const copy = connectedCopy(turn.content);
    if (copy) {
      messages = messages.map((m, i) => (i === connIdx ? { ...m, content: copy } : m));
      changed = true;
    }
    const header = messages[divIdx];
    if (header.dividerPending || (state.agentName && header.agentName !== state.agentName)) {
      messages = messages.map((m, i) => (i === divIdx
        ? { ...m, dividerPending: false, agentName: state.agentName || m.agentName || DEFAULT_REP }
        : m));
      changed = true;
    }
  }

  return {
    messages: changed ? messages : stored,
    changed,
    connectingId: messages[connIdx].id,
    enterHumanDivId: messages[divIdx].id,
  };
}
